import React, { useState, useEffect } from 'react';
import './AdminCommon.css';
import './adminArticles.css';
import { useArticle } from '../../hooks/useArticle';
import useImageUpload from '../../hooks/useImageUpload';
import TiptapEditor from '../ui/TiptapEditor/TiptapEditor';
import Modal from '../common/Modal/modal';
import { Trash2, Edit, Plus, X, ImagePlus } from 'lucide-react';

const normalizeTitle = (article) => article?.title || article?.Title || article?.name || article?.Name || '';
const normalizeAuthor = (article) => article?.author || article?.Author || '';
const normalizeThumbnails = (article) => {
  const thumbnails = article?.thumbnails || article?.Thumbnails || article?.thumbnail || article?.Thumbnail || [];
  const thumbnailArray = Array.isArray(thumbnails) ? thumbnails : [thumbnails];
  return [thumbnailArray[0] || '', thumbnailArray[1] || ''];
};
const normalizeImages = (article) => article?.imagesURL || article?.ImagesURL || [];
const normalizeContent = (article) => article?.content || article?.Content || article?.articleContent || article?.ArticleContent || '';
const normalizeExcerpt = (article) => article?.excerpt || article?.Excerpt || '';
const normalizeTags = (article) => article?.tags || article?.Tags || [];

function AdminArticles({ idToken }) {
  const { articles, loadingArticles, error, fetchArticles, addArticle, updateArticle, deleteArticle } = useArticle();
  const { handleImageUpload, uploading } = useImageUpload();

  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [formError, setFormError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author: '',
    tags: '',
    content: '',
    excerpt: '',
    thumbnails: ['', ''],
    imagesURL: ['', ''],
  });

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    if (editingArticle) {
      const thumbnails = normalizeThumbnails(editingArticle);
      const images = normalizeImages(editingArticle);
      setFormData({
        title: normalizeTitle(editingArticle),
        subtitle: editingArticle.subtitle || editingArticle.Subtitle || '',
        author: normalizeAuthor(editingArticle),
        tags: normalizeTags(editingArticle).join(', '),
        content: normalizeContent(editingArticle),
        excerpt: normalizeExcerpt(editingArticle),
        thumbnails,
        imagesURL: [
          images?.[0] || '',
          images?.[1] || '',
        ],
      });
      setShowForm(true);
    }
  }, [editingArticle]);

  const resetForm = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      subtitle: '',
      author: '',
      tags: '',
      content: '',
      excerpt: '',
      thumbnails: ['', ''],
      imagesURL: ['', ''],
    });
    setFormError('');
    setShowForm(false);
  };

  const handleThumbnailUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await handleImageUpload(file, idToken);
      if (url) {
        setFormData((prev) => {
          const thumbnails = [...prev.thumbnails];
          thumbnails[index] = url;
          return { ...prev, thumbnails };
        });
      }
    } catch (err) {
      setFormError(err.message || 'Upload failed');
    }
  };

  const handleImageUploadField = async (index, file) => {
    if (!file) return;
    try {
      const url = await handleImageUpload(file, idToken);
      if (url) {
        setFormData((prev) => {
          const imagesURL = [...prev.imagesURL];
          imagesURL[index] = url;
          return { ...prev, imagesURL };
        });
      }
    } catch (err) {
      setFormError(err.message || 'Upload failed');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const imagesURL = [...prev.imagesURL];
      imagesURL[index] = '';
      return { ...prev, imagesURL };
    });
  };

  const addImageField = () => {
    setFormData((prev) => ({
      ...prev,
      imagesURL: [...prev.imagesURL, ''],
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleContentChange = (html) => {
    setFormData((prev) => ({
      ...prev,
      content: html,
      excerpt: prev.excerpt || html.replace(/<[^>]+>/g, '').slice(0, 200),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const thumbnails = formData.thumbnails.filter(Boolean);
    if (!formData.title.trim() || !formData.content.trim() || !formData.author.trim()) {
      setFormError('Le titre, le contenu et l auteur sont requis.');
      return;
    }

    const images = formData.imagesURL.filter(Boolean);

    const articlePayload = {
      title: formData.title,
      subtitle: formData.subtitle,
      content: formData.content,
      excerpt: formData.excerpt || formData.content.slice(0, 200),
      Thumbnails: thumbnails.length > 0 ? thumbnails.slice(0, 2) : null,
      imagesURL: images.length > 0 ? images : null,
      author: formData.author,
      tags: formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    };

    try {
      if (editingArticle) {
        const success = await updateArticle(editingArticle.id, articlePayload, idToken);
        if (success) {
          resetForm();
          alert('Article mis à jour avec succès');
        }
      } else {
        await addArticle(articlePayload, idToken);
        resetForm();
        alert('Article créé avec succès');
      }
    } catch (err) {
      setFormError(err.message || 'Erreur lors de l enregistrement');
    }
  };

  const getDisplayArticle = (article) => {
    const images = normalizeImages(article);
    const thumbnails = normalizeThumbnails(article);
    return {
      title: normalizeTitle(article),
      author: normalizeAuthor(article),
      thumbnails,
      subtitle: article.subtitle || article.Subtitle || '',
      images,
    };
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
  };

  const handleDelete = async (id) => {
    try {
      await deleteArticle(id, idToken);
      setDeleteConfirmId(null);
    } catch (err) {
      setFormError(err.message || 'Erreur lors de la suppression');
    }
  };

  if (loadingArticles) {
    return <div className="admin-loading">Chargement des articles...</div>;
  }

  return (
    <div className="admin-common-container">
      <div className="admin-common-header">
        <h2>Gestion des articles</h2>
        <button className="admin-add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
          <Plus size={20} /> Nouvel article
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {formError && <div className="admin-error">{formError}</div>}

      <div className="admin-grid">
        {articles.map((article) => (
          <div key={article.id} className="admin-card">
            <div className="admin-card-image">
              {getDisplayArticle(article).thumbnails[0] ? <img src={getDisplayArticle(article).thumbnails[0]} alt={getDisplayArticle(article).title} /> : <div className="no-image"><ImagePlus size={36} /></div>}
            </div>
            <div className="admin-card-content">
              <h3>{getDisplayArticle(article).title}</h3>
              <p>{getDisplayArticle(article).author}</p>
              {getDisplayArticle(article).subtitle && <p className="admin-card-subtitle">{getDisplayArticle(article).subtitle}</p>}
              {getDisplayArticle(article).thumbnails.filter(Boolean).length > 0 && (
                <div className="admin-card-thumbnails admin-card-thumbnails--fixed">
                  {getDisplayArticle(article).thumbnails.filter(Boolean).slice(0, 2).map((image, index) => (
                    <img key={`${article.id}-thumb-${index}`} src={image} alt={`${getDisplayArticle(article).title} miniature ${index + 1}`} />
                  ))}
                </div>
              )}
              {getDisplayArticle(article).images.length > 0 && (
                <div className="admin-card-thumbnails">
                  {getDisplayArticle(article).images.slice(0, 2).map((image, index) => (
                    <img key={`${article.id}-image-${index}`} src={image} alt={`${getDisplayArticle(article).title} illustration ${index + 1}`} />
                  ))}
                </div>
              )}
              <div className="admin-card-actions">
                <button className="action-btn edit-btn" onClick={() => handleEdit(article)}><Edit size={16} /></button>
                <button className="action-btn delete-btn" onClick={() => setDeleteConfirmId(article.id)}><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {deleteConfirmId && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <p>Voulez-vous vraiment supprimer cet article ?</p>
            <div className="admin-modal-actions">
              <button onClick={() => setDeleteConfirmId(null)}>Annuler</button>
              <button className="danger" onClick={() => handleDelete(deleteConfirmId)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={showForm} onClose={resetForm}>
        <div className="admin-form-card admin-articles-modal-card">
          <div className="form-header">
            <h3>{editingArticle ? 'Modifier l article' : 'Créer un nouvel article'}</h3>
          </div>

          <form className="admin-form" onSubmit={handleSubmit}>
            <label>
              Titre *
              <input type="text" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required />
            </label>
            <label>
              Sous-titre
              <input type="text" value={formData.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} />
            </label>
            <label>
              Auteur *
              <input type="text" value={formData.author} onChange={(e) => handleChange('author', e.target.value)} required />
            </label>
            <label>
              Tags
              <input type="text" value={formData.tags} onChange={(e) => handleChange('tags', e.target.value)} placeholder="mode, mariage" />
            </label>
            <label>
              Contenu riche *
              <TiptapEditor value={formData.content} onChange={handleContentChange} placeholder="Rédigez le contenu de l article..." />
            </label>
            <label>
              Extrait
              <textarea value={formData.excerpt} onChange={(e) => handleChange('excerpt', e.target.value)} rows="3" />
            </label>

            <div className="media-fields">
              <div className="media-field-group">
                <h4>Miniatures</h4>
                {[0, 1].map((index) => (
                  <div className="media-field" key={`thumb-${index}`}>
                    <label>
                      Miniature {index + 1}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleThumbnailUpload(index, e)}
                        disabled={uploading}
                      />
                    </label>
                    {formData.thumbnails[index] && (
                      <div className="thumbnail-preview">
                        <img src={formData.thumbnails[index]} alt={`Miniature ${index + 1}`} />
                        <button type="button" onClick={() => handleChange('thumbnails', formData.thumbnails.map((value, thumbIndex) => (thumbIndex === index ? '' : value)))}><X size={16} /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="media-field-group">
                <div className="media-field-group-header">
                  <h4>Images d illustration</h4>
                  <button type="button" className="admin-add-btn admin-add-btn--secondary" onClick={addImageField} disabled={uploading}>
                    <Plus size={16} /> Ajouter une image
                  </button>
                </div>
                {formData.imagesURL.map((_, index) => (
                <div className="media-field" key={index}>
                  <label>
                    Image d illustration {index + 1}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUploadField(index, e.target.files?.[0])}
                      disabled={uploading}
                    />
                  </label>
                  {formData.imagesURL[index] && (
                    <div className="thumbnail-preview">
                      <img src={formData.imagesURL[index]} alt={`Illustration ${index + 1}`} />
                      <button type="button" onClick={() => handleRemoveImage(index)}><X size={16} /></button>
                    </div>
                  )}
                </div>
                ))}
              </div>
            </div>

            <button type="submit" className="admin-submit-btn" disabled={uploading}>
              {editingArticle ? 'Mettre à jour' : 'Créer'}
            </button>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default AdminArticles;
